import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Switch,
  ButtonGroup,
} from "@material-ui/core";
import { toast } from "react-toastify";

const DAYS = [
  { key: "seg", label: "Segunda-feira" },
  { key: "ter", label: "Terça-feira" },
  { key: "qua", label: "Quarta-feira" },
  { key: "qui", label: "Quinta-feira" },
  { key: "sex", label: "Sexta-feira" },
  { key: "sab", label: "Sábado" },
  { key: "dom", label: "Domingo" },
];

const defaultDays = () => {
  const d = {};
  DAYS.forEach((x) => {
    d[x.key] = { active: false, start: "09:00", end: "18:00" };
  });
  return d;
};

const FlowBuilderSmartIntervalModal = ({ open, onSave, onUpdate, data, close }) => {
  const [activeModal, setActiveModal] = useState(false);
  const [mode, setMode] = useState("interval");
  const [amount, setAmount] = useState("5");
  const [unit, setUnit] = useState("min");
  const [dateTime, setDateTime] = useState("");
  const [days, setDays] = useState(defaultDays());

  useEffect(() => {
    if (open === "edit") {
      const d = data?.data || data || {};
      setMode(d.mode || "interval");
      setAmount(String(d.amount || 5));
      setUnit(d.unit || "min");
      setDateTime(d.dateTime || "");
      setDays({ ...defaultDays(), ...(d.days || {}) });
      setActiveModal(true);
    } else if (open === "create") {
      setMode("interval");
      setAmount("5");
      setUnit("min");
      setDateTime("");
      setDays(defaultDays());
      setActiveModal(true);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const setDay = (key, patch) =>
    setDays((old) => ({ ...old, [key]: { ...old[key], ...patch } }));

  const handleSave = () => {
    if (mode === "interval" && (!Number(amount) || Number(amount) <= 0)) {
      toast.error("Informe o tempo de espera.");
      return;
    }
    if (mode === "date" && !dateTime) {
      toast.error("Escolha a data e hora.");
      return;
    }
    if (mode === "schedule" && !DAYS.some((d) => days[d.key]?.active)) {
      toast.error("Ative ao menos um dia da semana.");
      return;
    }
    const payload = {
      mode,
      amount: Number(amount) || 0,
      unit,
      dateTime,
      days,
    };
    if (open === "edit") {
      onUpdate({ ...data, data: { ...(data?.data || {}), ...payload } });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  const tabBtn = (value, label) => (
    <Button
      onClick={() => setMode(value)}
      variant={mode === value ? "contained" : "outlined"}
      color={mode === value ? "primary" : "default"}
    >
      {label}
    </Button>
  );

  return (
    <Dialog open={activeModal} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {open === "create" ? "Adicionar Intervalo Inteligente" : "Editar Intervalo Inteligente"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 6 }}>
          TIPO DE AGENDAMENTO
        </Typography>
        <ButtonGroup size="small" style={{ marginBottom: 16 }}>
          {tabBtn("interval", "Intervalo")}
          {tabBtn("date", "Data")}
          {tabBtn("schedule", "Horários")}
        </ButtonGroup>

        {mode === "interval" && (
          <Box style={{ display: "flex", gap: 8 }}>
            <TextField
              label="Esperar"
              type="number"
              size="small"
              variant="outlined"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 1 }}
              style={{ flex: 1 }}
            />
            <TextField
              select
              label="Unidade"
              size="small"
              variant="outlined"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              style={{ minWidth: 150 }}
            >
              <MenuItem value="seg">Segundos</MenuItem>
              <MenuItem value="min">Minutos</MenuItem>
              <MenuItem value="hora">Horas</MenuItem>
              <MenuItem value="dia">Dias</MenuItem>
            </TextField>
          </Box>
        )}

        {mode === "date" && (
          <TextField
            label="Continuar em"
            type="datetime-local"
            size="small"
            variant="outlined"
            value={dateTime}
            onChange={(e) => setDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="O fluxo fica parado até essa data/hora (horário de Brasília)."
          />
        )}

        {mode === "schedule" && (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Dias e horários
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 10 }}>
              O fluxo aguarda até entrar em um dos horários ativos. Se já estiver
              dentro da faixa, continua na hora.
            </Typography>
            {DAYS.map((d) => (
              <Box
                key={d.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <Switch
                  size="small"
                  color="primary"
                  checked={!!days[d.key]?.active}
                  onChange={(e) => setDay(d.key, { active: e.target.checked })}
                />
                <Typography variant="body2" style={{ flex: 1 }}>
                  {d.label}
                </Typography>
                {days[d.key]?.active ? (
                  <>
                    <TextField
                      type="time"
                      size="small"
                      variant="outlined"
                      value={days[d.key]?.start || "09:00"}
                      onChange={(e) => setDay(d.key, { start: e.target.value })}
                      style={{ width: 110 }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      variant="outlined"
                      value={days[d.key]?.end || "18:00"}
                      onChange={(e) => setDay(d.key, { end: e.target.value })}
                      style={{ width: 110 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    Inativo
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {open === "create" ? "Adicionar" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderSmartIntervalModal;
